import React, { useState, useEffect } from 'react';
import { Button } from "./button";
import { Loader2, RefreshCw, KeyRound } from "lucide-react";
import { useToast } from "./use-toast";
import { Checkbox } from './checkbox';
import { Label } from './label';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import StravaConnectButtonImage from './img/btn_strava_connectwith_orange.svg';
import { trackActivityEvents } from '../utils/ga4Events';

const stravaBaseUrl = process.env.REACT_APP_API_URL ?
    (process.env.REACT_APP_API_URL.includes('localhost') ?
        `${process.env.REACT_APP_API_URL}/api/strava` :
        `${process.env.REACT_APP_API_URL}/strava`) :
    '/api/strava';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

const StravaComponent = ({ stravaStatus, onStatusUpdate, filteredActivities, onSetFilteredActivities, fromDate, toDate, onFromDateChange, onToDateChange, allDates, onAllDatesChange }) => {
    const { toast } = useToast();

    const [isLocalLoading, setLocalLoading] = useState(false);
    const [commutes, setCommutes] = useState(false);
    const [allDatesLocal, setAllDatesLocal] = useState(allDates);
    const [fromDateLocal, setFromDateLocal] = useState(fromDate);
    const [toDateLocal, setToDateLocal] = useState(toDate);
    const [lastSyncDate, setLastSyncDate] = useState(null);

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.origin === 'http://192.168.179.203:5000' || event.origin.includes('localhost:5000')) {
                if (event.data === 'stravaConnected') {
                    // setLocalLoading(true);
                    if (onStatusUpdate) {
                        onStatusUpdate(prev => ({
                            ...prev,
                            isLoading: true
                        }));
                        setLocalLoading(true);
                    }
                } else if (event.data === 'stravaError') {
                    trackActivityEvents.stravaConnectError();
                    // setLocalLoading(false);
                    toast({
                        title: "Error",
                        description: "An error connecting to Strava",
                        variant: "destructive",
                        duration: 3000,
                    });
                }
            }
        };

        window.addEventListener('message', handleMessage);
        checkStravaStatus();
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    useEffect(() => {
        if (stravaStatus.isLoading || isLocalLoading) {
            startPollingForCompletion();
        }
    }, [stravaStatus.isLoading, isLocalLoading]);

    const checkStravaStatus = async () => {
        const token = localStorage.getItem('token');
        if (!token) return;

        try {
            const response = await fetch(`${API_URL}/user/strava-data-status`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            // If we get a 405, set not connected
            if (response.status === 405) {
                if (onStatusUpdate) {
                    onStatusUpdate({
                        isConnected: false,
                        isLoading: false,
                        numActivities: 0,
                        activities: null,
                        lastSync: null,
                    });
                }
                setLocalLoading(false);
                return;
            }

            const data = await response.json();

            if (data.lastSync) {
                setLastSyncDate(new Date(data.lastSync));
            }

            if (onStatusUpdate) {
                onStatusUpdate({
                    isConnected: data.isConnected,
                    isLoading: data.isLoading,
                    activities: data.activities,
                    numActivities: data.numActivities,
                    lastSync: data.lastSync || (data.activities ? new Date().toISOString() : null),
                });
                setLocalLoading(data.isLoading);
            }

            // setLocalLoading(false);
            return data;
        } catch (error) {
            if (onStatusUpdate) {
                onStatusUpdate({
                    isConnected: false,
                    isLoading: false,
                    activities: null,
                    numActivities: 0,
                    lastSync: null,
                });
                setLocalLoading(false);
            }
        }
    };

    const startPollingForCompletion = () => {
        let pollingCount = 0;
        const MAX_POLLING_ATTEMPTS = 48; // Poll for up to 4 minutes (24 * 10s = 240s)

        const pollInterval = setInterval(async () => {
            pollingCount++;

            try {
                const latestStatus = await checkStravaStatus();
                // If data is loaded or no longer loading, stop polling
                if (latestStatus && latestStatus.isLoading === false) {
                    clearInterval(pollInterval);
                }


                // Stop polling after max attempts
                if (pollingCount >= MAX_POLLING_ATTEMPTS) {
                    clearInterval(pollInterval);
                    toast({
                        title: "Information",
                        description: "Strava sync is taking longer than expected. Please refresh the page!",
                        variant: "warning",
                        duration: 5000,
                    });
                }
            } catch (error) {
                console.error("Error during Strava polling:", error);
            }
        }, 5000);
    };

    const handleStravaConnect = () => {
        const token = localStorage.getItem('token');
        setLocalLoading(true);
        trackActivityEvents.stravaConnectStart();
        const stravaWindow = window.open(`${stravaBaseUrl}/connect?token=${token}`, '_blank');
        if (!stravaWindow) {
            toast({
                title: "Error",
                description: "Please allow popups to connect with Strava",
                variant: "destructive",
                duration: 3000,
            });
            return;
        }
    };

    const handleRefresh = async () => {
        const token = localStorage.getItem('token');
        if (!token) return;

        toast({
            title: "Refreshing",
            description: "Fetching latest Strava activities...",
            duration: 3000,
        });

        try {
            // Set loading state
            if (onStatusUpdate) {
                onStatusUpdate(prev => ({
                    ...prev,
                    isLoading: true
                }));
            }
            trackActivityEvents.stravaRefreshStart();
            // Call the refresh endpoint with the last activity date
            const response = await fetch(`${stravaBaseUrl}/refresh`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    // If we have a lastSyncDate, send it as a timestamp
                    afterDate: lastSyncDate ? Math.floor(lastSyncDate.getTime() / 1000) : null
                })
            });

            if (!response.ok) {
                trackActivityEvents.stravaRefreshError();
                throw new Error('Failed to refresh Strava data');
            }

            // Start polling for completion
            startPollingForCompletion();

        } catch (error) {
            console.error("Error refreshing Strava data:", error);
            toast({
                title: "Error",
                description: "Failed to refresh Strava data",
                variant: "destructive",
                duration: 3000,
            });

            // Reset loading state
            if (onStatusUpdate) {
                onStatusUpdate(prev => ({
                    ...prev,
                    isLoading: false
                }));
            }
        }
    };

    const handleActivitySelectionChange = (selectedActivities) => {
        if (onSetFilteredActivities) {
            onSetFilteredActivities(selectedActivities);
        }
    };

    // Date filter handlers
    const handleCommuteChanged = (checked) => {
        setCommutes(checked);
    };

    const handleAllDatesChangeLocal = (checked) => {
        setAllDatesLocal(checked);
        onAllDatesChange(checked);
    };

    const handleFromDateChangeLocal = (date) => {
        setFromDateLocal(date);
        onFromDateChange(date);
    };

    const handleToDateChangeLocal = (date) => {
        setToDateLocal(date);
        onToDateChange(date);
    };

    if (stravaStatus.isLoading || isLocalLoading) {
        return (
            <div className="flex flex-col items-center space-y-4">
                <div className="flex items-center justify-center w-80 h-12 bg-gray-100 rounded-2xl">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    <span className="text-gray-700">Syncing Strava activities...</span>
                </div>

                {stravaStatus.numActivities > 0 && (
                    <div className="w-80 flex flex-col items-center">
                        <p className="text-sm text-gray-600">
                            Already synced {stravaStatus.numActivities} activities. Please wait!
                        </p>
                    </div>
                )}

                <span className="text-gray-700 text-center">
                    This may take up to 2 minutes. Activities will appear automatically when ready.
                </span>
            </div>
        );
    }

    // If connected, show a compact sync header. Per-activity selection + date/
    // commute filters live in the ActivityFilterPanel mounted by the dashboard.
    if (stravaStatus.activities) {
        return (
            <div className="w-full flex justify-between items-center gap-2 text-sm text-gray-700">
                <div>
                    <span className="font-semibold">{stravaStatus.numActivities}</span> activities synced
                    {lastSyncDate && (
                        <span className="ml-2 text-xs text-gray-500">
                            Last sync: {new Date(lastSyncDate).toLocaleDateString()}
                        </span>
                    )}
                </div>
                <div className="flex items-center space-x-1">
                    <Button onClick={handleRefresh} className="p-1" variant="ghost" title="Refresh Strava data">
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button onClick={handleStravaConnect} className="p-1" variant="ghost" title="Reconnect Strava (if you changed password)">
                        <KeyRound className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center">
            <Button
                onClick={handleStravaConnect}
                className="w-full h-auto p-0 overflow-hidden rounded-md hover:opacity-90 transition-opacity"
                style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    boxShadow: 'none'
                }}
            >
                <img
                    src={StravaConnectButtonImage}
                    alt="Connect with Strava"
                    className="w-full h-full object-cover"
                />
            </Button>
            <span>Sync will take up to 2 minutes!</span>
        </div>
    );
};

export default StravaComponent;